import moment, { Moment } from 'moment';
import calculateSchedule from '@api/features/fixtures/utils/calculateSchedule';
import PeriodScheduleItem from '@api/models/period-schedule-item';
import calculateMinMaxDurationDays from '@api/features/fixtures/utils/calculateMinMaxDurationDays';

const getExactDurationStr = (maxDays: number) => {
	return maxDays > 0 ? `${maxDays} ${maxDays > 1 ? 'days' : 'day'}` : '';
};

const formatTcAndBbFixtureDuration = ({
	items,
	laycanTo,
	laycanFrom,
}: {
	items: PeriodScheduleItem[];
	laycanTo: Moment | null;
	laycanFrom: Moment | null;
}) => {
	if (items == null || laycanTo == null) {
		return null;
	}

	const schedule = calculateSchedule({
		items,
		laycanTo,
		useVariance: false,
	});

	const lastEntry = schedule[schedule.length - 1];

	if (lastEntry == null) {
		return null;
	}

	const {
		durationMinimumUnit: minUnit,
		durationMinimumVariance: minVariance,
		durationMaximumUnit: maxUnit,
		durationMaximumVariance: maxVariance,
		minimumDurationExact: minExact,
		maximumDurationExact: maxExact,
	} = lastEntry;

	let maxDuration;
	let minDuration;

	const [minDays, maxDays] = calculateMinMaxDurationDays({
		items,
		laycanTo,
		laycanFrom,
		useVariance: false,
	});

	if (minExact != null && laycanTo != null) {
		minDuration = getExactDurationStr(minDays ?? 0);
	}

	if (maxExact != null && laycanFrom != null) {
		maxDuration = getExactDurationStr(maxDays ?? 0);
	}

	const minVarianceString = (minVariance === 0 || minVariance === null) ?
		'' :
		` ± ${minVariance} days`;
	const maxVarianceString = (maxVariance === 0 || maxVariance === null) ?
		'' :
		` ± ${maxVariance} days`;

	type UnitOfTime = 'days' | 'months' | 'years';

	const formatDuration = (
		value: number | null,
		duration: string | undefined,
		unit: UnitOfTime,
		varianceString: string,
	): string | null => {
		if (!duration && value != null) {
			const units = {
				days: value,
				months: Math.round(moment.duration(value, 'days').asMonths()),
				years: Math.round(moment.duration(value, 'days').asYears()),
			};

			const formattedValue = units[unit ?? 'days'];

			return `${formattedValue} ${unit?.slice(0, -1) ?? 'day'}${formattedValue === 1 ? '' : 's'}${varianceString}`;
		}

		return duration || null;
	};

	const minStr = formatDuration(minDays, minDuration, minUnit as UnitOfTime, minVarianceString);
	const maxStr = formatDuration(maxDays, maxDuration, maxUnit as UnitOfTime, maxVarianceString);

	if (minStr == null && maxStr == null) {
		return null;
	}

	return `${minStr ?? ''} - ${maxStr ?? ''}`;
};

export default formatTcAndBbFixtureDuration;
